/**
 * Sections and checkpoints (GDD §4.5, §4.6, §5.3; decision Q2). A section is built from its own seed when a run
 * starts or a bridge falls; the checkpoint is the run start or 150 m past the last destroyed bridge. After a crash
 * (GDD rules) the jet restarts at the checkpoint and the section is rebuilt from the same seed and range, so its
 * enemies and barges return exactly as they were.
 */
import { config } from '../core/config.ts';
import { spawnSection } from '../difficulty/spawner.ts';
import type { GameSim } from '../sim/context.ts';

/** The first section of a run starts 170 m ahead of the jet (concept L1142). */
const RUN_START_AHEAD = 170;
/** A section ends 40 m before the bridge that closes it (concept L1076, L1142). */
const BEFORE_BRIDGE = 40;

/** Section 0 of a run: from 170 m ahead of the jet to the next bridge (concept newGame, L1142). */
export function beginRunSection(sim: GameSim): void {
  const run = sim.run;
  setRunStart(sim);
  sim.streams.beginSection(run.runSeed, run.sectionIndex);
  spawnSection(sim, run.sectionZA, run.sectionZB);
}

/**
 * Makes the jet's position the run start: section 0 from 170 m ahead to 40 m before the next bridge, the checkpoint
 * and the progress bar's origin (D19) here. Spawns nothing.
 */
export function setRunStart(sim: GameSim): void {
  const run = sim.run;
  const b = sim.bridge;
  const z = sim.jet.z;
  run.sectionIndex = 0;
  run.checkpointZ = z;
  run.progressFrom = z;
  run.sectionZA = z - RUN_START_AHEAD;
  run.sectionZB = z > b.z + 100 ? b.z + BEFORE_BRIDGE : b.z - config.spawning.sectionLength + BEFORE_BRIDGE;
}

/** The next section, after a bridge falls: spawned from 150 m past it (concept L1076); the checkpoint moves there. */
export function beginNextSection(sim: GameSim, zA: number, zB: number, checkpointZ: number): void {
  const run = sim.run;
  run.sectionIndex++;
  run.checkpointZ = checkpointZ;
  run.sectionZA = zA;
  run.sectionZB = zB;
  sim.streams.beginSection(run.runSeed, run.sectionIndex);
  spawnSection(sim, zA, zB);
}

/** Rebuilds the current section from its seed (a checkpoint restart, Q2). */
export function rebuildSection(sim: GameSim): void {
  const run = sim.run;
  sim.jetTrigCount = 0;
  sim.streams.beginSection(run.runSeed, run.sectionIndex);
  spawnSection(sim, run.sectionZA, run.sectionZB);
}

/** The 800 m floating-origin shift moves the checkpoint and the section's range with the world. */
export function shiftCheckpoint(sim: GameSim, dz: number): void {
  const run = sim.run;
  run.checkpointZ += dz;
  run.progressFrom += dz;
  run.sectionZA += dz;
  run.sectionZB += dz;
}

/**
 * The HUD's progress bar (GDD §7.2, D19): 0 at the last destroyed bridge (or the run start), 1 at the next bridge.
 * A destroyed bridge still ahead of the jet is behind the run's progress, so the bar waits at 0 until the jet
 * crosses it.
 */
export function runProgress(sim: GameSim): number {
  const out = { progress: 0 };
  writeRunProgress(sim, out);
  return out.progress;
}

/** runProgress written into `out` (the HUD's per-frame path: a field write, so no number is boxed, GDD §10.5). */
export function writeRunProgress(sim: GameSim, out: { progress: number }): void {
  const b = sim.bridge;
  const z = sim.jet.z;
  const next = b.intact && z > b.z ? b.z : b.z - config.spawning.sectionLength;
  const from = sim.run.progressFrom;
  const span = from - next;
  const p = span > 1 ? (from - z) / span : 0;
  out.progress = p < 0 ? 0 : p > 1 ? 1 : p;
}
